import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { type ClientNutritionDay } from "@fitness-app/data-models/entities/ClientNutritionDay";
import { MealIngredientStatus, type NutritionPlanDay } from "@fitness-app/data-models/entities/MealsPlan";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { supabase } from "~/store/initializeStore";
import { logSimulatorWrite, SIMULATOR_TABLE } from "./simulatorWriteLog";

dayjs.extend(isoWeek);

/**
 * Kolejność jak w `getNutritionDay` — dzień planu dobiera się po nazwie, nie po indeksie tablicy.
 * Tablica celowo zachowuje kształt 1:1 z tamtą: `isoWeekday()` zwraca 1–7, więc indeks 0 nigdy nie
 * jest czytany, ale skracanie jej tutaj rozjechałoby obie listy przy następnym porównaniu.
 */
const WEEKDAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export type SimulatorNutritionDay = {
  /** Dzień pochodzący z trackera klientki, czy dopiero z jej planu żywieniowego. */
  source: "tracked" | "mealsPlan";
  day: NutritionPlanDay;
  eventDate: string;
  clientMealsPlanId: string | null;
  /** Ile posiłków ma choć jedno danie oznaczone jako zjedzone — tyle liczy `client_activity`. */
  trackedMeals: number;
  totalMeals: number;
};

const countTrackedMeals = (day: NutritionPlanDay): number =>
  (day.meals ?? []).filter((meal) => (meal.dishes ?? []).some((dish) => dish.status === MealIngredientStatus.EATEN))
    .length;

/**
 * Dzień diety dla wskazanej daty — najpierw z trackera (`client_nutrition_day`), a jeśli
 * klientka jeszcze nic nie odhaczyła, z jej aktywnego planu, po nazwie dnia tygodnia.
 * To ta sama kolejność, którą stosuje selektor `getNutritionDay` w aplikacji klienta.
 */
export const resolveNutritionDay = async (
  traineeId: string,
  activeMealsPlanId: string | null,
  date: string,
): Promise<SimulatorNutritionDay | null> => {
  const { data: tracked, error } = await supabase
    .from("client_nutrition_day")
    .select("id, day, eventDate, clientMealsPlanId")
    .eq("traineeId", traineeId)
    .eq("eventDate", date)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  if (tracked) {
    const day = tracked.day as unknown as NutritionPlanDay;
    return {
      source: "tracked",
      day,
      eventDate: date,
      clientMealsPlanId: tracked.clientMealsPlanId,
      trackedMeals: countTrackedMeals(day),
      totalMeals: (day.meals ?? []).length,
    };
  }

  if (!activeMealsPlanId) {
    return null;
  }

  const { data: plan, error: planError } = await supabase
    .from("client_meals_plan_details")
    .select("planId, weeks")
    .eq("planId", activeMealsPlanId)
    .maybeSingle();

  if (planError) {
    throw new Error(planError.message);
  }

  const weeks = (plan?.weeks ?? []) as unknown as { weekNumber: number; days: NutritionPlanDay[] }[];
  const weekdayName = WEEKDAY_NAMES[dayjs(date).isoWeekday()];
  const planDay = weeks[0]?.days.find((day) => day.name === weekdayName);

  if (!planDay) {
    return null;
  }

  return {
    source: "mealsPlan",
    day: planDay,
    eventDate: date,
    clientMealsPlanId: activeMealsPlanId,
    trackedMeals: countTrackedMeals(planDay),
    totalMeals: (planDay.meals ?? []).length,
  };
};

/**
 * Klucz dania w obrębie dnia. Celowo po INDEKSIE, nie po `dish.id`: ten sam przepis może
 * wystąpić w posiłku dwa razy, a wtedy id nie rozróżnia porcji.
 */
export const dishKey = (mealId: string, dishIndex: number) => `${mealId}:${dishIndex}`;

export type SimulatorDish = {
  key: string;
  name: string;
  eaten: boolean;
};

export type SimulatorMeal = {
  id: string;
  name: string;
  dishes: SimulatorDish[];
  /** Posiłek liczy się do `client_activity`, gdy ma choć jedno zjedzone danie. */
  tracked: boolean;
};

const dishName = (dish: { name?: string | null }, index: number) => dish.name || `Pozycja ${index + 1}`;

/** Rozbicie dnia na posiłki i dania — to, co klientka widzi i odhacza pojedynczo. */
export const toSimulatorMeals = (day: NutritionPlanDay): SimulatorMeal[] =>
  (day.meals ?? []).map((meal) => {
    const dishes = (meal.dishes ?? []).map((dish, index) => ({
      key: dishKey(meal.id, index),
      name: dishName(dish, index),
      eaten: dish.status === MealIngredientStatus.EATEN,
    }));

    return {
      id: meal.id,
      name: meal.name || meal.type || "Posiłek",
      dishes,
      tracked: dishes.some((dish) => dish.eaten),
    };
  });

/** Zbiór kluczy dań aktualnie oznaczonych jako zjedzone. */
export const eatenDishKeys = (day: NutritionPlanDay): Set<string> =>
  new Set(
    (day.meals ?? []).flatMap((meal) =>
      (meal.dishes ?? [])
        .map((dish, index) => (dish.status === MealIngredientStatus.EATEN ? dishKey(meal.id, index) : null))
        .filter((key): key is string => key !== null),
    ),
  );

/** Przepisuje dzień tak, żeby zjedzone były dokładnie dania z `eatenKeys`. */
const applyDishStatuses = (day: NutritionPlanDay, eatenKeys: Set<string>): NutritionPlanDay => ({
  ...day,
  updatedAt: new Date().toISOString(),
  meals: (day.meals ?? []).map((meal) => ({
    ...meal,
    dishes: (meal.dishes ?? []).map((dish, index) => ({
      ...dish,
      status: eatenKeys.has(dishKey(meal.id, index)) ? MealIngredientStatus.EATEN : MealIngredientStatus.EMPTY,
    })),
  })),
});

/** Klucze wszystkich dań w dniu — pod „odhacz wszystko". */
export const allDishKeys = (day: NutritionPlanDay): Set<string> =>
  new Set((day.meals ?? []).flatMap((meal) => (meal.dishes ?? []).map((_dish, index) => dishKey(meal.id, index))));

type TrackParams = {
  nutritionDay: SimulatorNutritionDay;
  traineeId: string;
  userId: string | null;
  createdBy: string;
  date: string;
  /** Dokładny zbiór dań, które mają zostać zjedzone — reszta wraca do `empty`. */
  eatenKeys: Set<string>;
  mode: "single" | "bulk";
};

/**
 * Zapisuje odhaczone posiłki tą samą drogą co klientka — RPC `upsert_nutrition_tracked_day`.
 *
 * Uwaga: ten RPC ma `exception when others` i zamiast podnieść błąd zwraca go w polu `error`
 * swojego JSON-a. Odmowa RLS wygląda stąd jak udany zapis, dlatego sprawdzamy oba miejsca.
 */
export const trackNutritionDay = async ({
  nutritionDay,
  traineeId,
  userId,
  createdBy,
  date,
  eatenKeys,
  mode,
}: TrackParams): Promise<void> => {
  const selectedDay = dayjs(date, "YYYY-MM-DD");

  const payload: Partial<ClientNutritionDay> = {
    id: generateUniqId(),
    eventDate: date,
    eventWeek: `${selectedDay.format("YYYY")}-${selectedDay.isoWeek()}`,
    traineeId,
    userId,
    clientMealsPlanId: nutritionDay.clientMealsPlanId,
    day: applyDishStatuses(nutritionDay.day, eatenKeys),
  };

  const { data, error } = await supabase.rpc("upsert_nutrition_tracked_day", {
    p_nutrition_day: payload as never,
  });

  if (error) {
    throw new Error(error.message);
  }

  const result = data as unknown as { data: { id: string } | null; error: { message: string } | null };

  if (result?.error) {
    throw new Error(result.error.message);
  }

  if (!result?.data?.id) {
    throw new Error("Zapis dnia diety nie zwrócił wiersza");
  }

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.nutritionDay,
    rowId: result.data.id,
    eventDate: date,
    mode,
    // Dzień z trackera istniał przed symulatorem — czyszczenie ma przywrócić odhaczenia
    // klientki, a nie skasować jej wpis.
    op: nutritionDay.source === "tracked" ? "update" : "insert",
    before: nutritionDay.source === "tracked" ? { day: nutritionDay.day } : undefined,
    label: eatenKeys.size > 0 ? `Odhaczone dania: ${eatenKeys.size}` : "Posiłki wyczyszczone",
  });
};
